import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Container,
  Card,
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { ShimmerTable } from "../common/ShimmerTable";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { isMobile } from "react-device-detect";
import moment from "moment";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import { FaBan, FaSearch } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jwt_decode from "jwt-decode";

const BlockedLeadsList = () => {
  const navigate = useNavigate();
  const [body, setBody] = useState([]);
  const [lead, setLead] = useState([]);
  const [isSpinner, setIsSpinner] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );

  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole !== "ADMIN" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      const numberString = String(number);
      if (numberString.length <= 5) return numberString;
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };

  const formatDate = (date) => {
    return moment(date).format("DD MMM YYYY");
  };

  const normalizeRow = (item, index) => {
    const firstname = item.firstname || "";
    const lastname = item.lastname || "";
    const leadname = [firstname, lastname].filter(Boolean).join(" ") || "—";
    const full_number = `${item.country_code || ""}${item.whatsapp_number || ""}`;
    return {
      ...item,
      serial: index + 1,
      leadname,
      full_number,
    };
  };

  const loadBlockedLeads = async () => {
    setIsSpinner(false);
    try {
      const res = await WhatsAppAPI.fetchBlockedLeads();
      if (res && res.success) {
        const records = res.records || [];
        const withSerial = records.map(normalizeRow);
        setBody(withSerial);
        setLead(withSerial);
      } else {
        setBody([]);
        setLead([]);
        toast.error(res?.message || "Failed to load blocked leads.");
      }
    } catch (err) {
      console.error(err);
      setBody([]);
      setLead([]);
      toast.error("Error loading blocked leads.");
    } finally {
      setIsSpinner(true);
    }
  };

  useEffect(() => {
    loadBlockedLeads();
  }, []);

  useEffect(() => {
    if (!lead || lead.length === 0) return;
    let filtered = [...lead];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (row) =>
          row.leadname?.toLowerCase().includes(q) ||
          row.email?.toLowerCase().includes(q) ||
          row.whatsapp_number?.toString().includes(searchQuery) ||
          row.full_number?.toString().includes(searchQuery) ||
          row.firstname?.toLowerCase().includes(q) ||
          row.lastname?.toLowerCase().includes(q),
      );
    }
    setBody(filtered);
  }, [searchQuery, lead]);

  const handleUnblock = async (leadId) => {
    if (window.confirm("Are you sure you want to unblock this lead?")) {
      try {
        const res = await WhatsAppAPI.unblockLead(leadId);
        if (res && res.success) {
          toast.success(res.message || "Lead unblocked successfully");
          setSelectedRows((prev) => prev.filter((id) => id !== leadId));
          loadBlockedLeads();
        } else {
          toast.error(res?.message || "Failed to unblock lead");
        }
      } catch (err) {
        console.error("Error unblocking lead:", err);
        toast.error("Failed to unblock lead");
      }
    }
  };

  const handleBulkUnblock = async () => {
    if (selectedRows.length === 0) return;
    if (
      window.confirm(`Are you sure you want to unblock the selected ${selectedRows.length} lead(s)?`)
    ) {
      try {
        const res = await WhatsAppAPI.bulkUnblockLeads({ ids: selectedRows });
        if (res && res.success) {
          toast.success(res.message || "Leads unblocked successfully");
          setSelectedRows([]);
          loadBlockedLeads();
        } else {
          toast.error(res?.message || "Failed to unblock leads");
        }
      } catch (err) {
        console.error("Error bulk unblocking leads:", err);
        toast.error("Failed to unblock leads");
      }
    }
  };

  const handleViewChat = (row) => {
    navigate("/whatsapp_messenger", { state: { openLead: row } });
  };

  const header = useMemo(() => {
    const cols = [];
    if (!isMobile) {
      cols.push(
        {
          title: (
            <Form.Check
              type="checkbox"
              id="select-all-blocked"
              checked={body.length > 0 && selectedRows.length === body.length}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedRows(body.map((r) => r.id));
                } else {
                  setSelectedRows([]);
                }
              }}
            />
          ),
          prop: "select",
          cell: (row) => (
            <Form.Check
              type="checkbox"
              id={`row-checkbox-${row.id}`}
              checked={selectedRows.includes(row.id)}
              onChange={(e) => {
                const checked = e.target.checked;
                setSelectedRows((prev) =>
                  checked
                    ? [...prev, row.id]
                    : prev.filter((id) => id !== row.id),
                );
              }}
            />
          ),
        },
        { title: "S.No", prop: "serial" },
        {
          title: "Name",
          prop: "leadname",
          isFilterable: true,
        },
        {
          title: "WhatsApp",
          prop: "whatsapp_number",
          isFilterable: true,
          cell: (row) => (
            <span>
              {row.country_code}{" "}
              {row.display_whatsapp_number ||
                formatPhoneNumber(row.whatsapp_number)}
            </span>
          ),
        },
        {
          title: "Created At",
          prop: "createddate",
          cell: (row) => formatDate(row.createddate),
        },
        {
          title: "Actions",
          prop: "actions",
          cell: (row) => (
            <div className="d-flex gap-2">
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => handleUnblock(row.id)}
              >
                Unblock
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleViewChat(row)}
              >
                View History
              </Button>
            </div>
          ),
        }
      );
    } else {
      cols.push({
        title: "Info",
        prop: "leadname",
        cell: (row) => (
          <div className="mobilecard">
            <Link
              to={"/leads/" + row.id}
              state={row}
              style={{ fontSize: "1.2rem" }}
            >
              {row.leadname || "—"}
            </Link>
            <span>
              {row.display_whatsapp_number ||
                formatPhoneNumber(row.whatsapp_number)}
            </span>
            <div className="mt-2 text-muted small">
              {formatDate(row.createddate)}
            </div>
            <div className="mt-2 d-flex gap-2">
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => handleUnblock(row.id)}
              >
                Unblock
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleViewChat(row)}
              >
                View History
              </Button>
            </div>
          </div>
        ),
      });
    }
    return cols;
  }, [selectedRows, body]);

  const labels = { beforeSelect: " " };

  return (
    <>
      <Container className="mt-5 mb-5">
        <Row className="mx-5 mb-4">
          <Card className="p-4 bg-white shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">
              <FaBan className="me-2 text-danger" /> OTP Out / Blocked Leads
            </h5>
            <small>
              Leads who have blocked us or are OTP out (read-only list).
            </small>
          </Card>
        </Row>

        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <div className="bg-white shadow-sm border-0 rounded-3 p-4">
              <h5 className="fw-semibold mb-1">Blocked leads</h5>

              {isSpinner ? (
                <DatatableWrapper
                  body={body}
                  keyField="id"
                  headers={header}
                  paginationOptionsProps={{
                    initialState: {
                      rowsPerPage: 10,
                      options: [10, 20, 30, 50],
                    },
                  }}
                >
                  <Row className="mb-3 align-items-center">
                    <Col
                      lg={4}
                      sm={8}
                      xs={8}
                      className="d-flex align-items-end"
                    >
                      <Filter className="p-2" />
                    </Col>
                    <Col lg={3} sm={4} xs={4}>
                      <PaginationOptions labels={labels} />
                    </Col>
                    <Col lg={5} className="d-flex justify-content-end gap-2">
                      <Button
                        variant="outline-success"
                        disabled={selectedRows.length === 0}
                        onClick={handleBulkUnblock}
                      >
                        Unblock Selected ({selectedRows.length})
                      </Button>
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    <Table
                      striped
                      bordered
                      className="related-list-table"
                      responsive="sm"
                    >
                      <TableHeader />
                      <TableBody />
                    </Table>
                  </div>
                  <Pagination />
                </DatatableWrapper>
              ) : (
                <ShimmerTable row={10} col={4} />
              )}
            </div>
          </Col>
        </Row>
      </Container>

      <ToastContainer autoClose={30000} />
    </>
  );
};

export default BlockedLeadsList;
